import React, { useState, useEffect, Fragment } from 'react';
import { useBranch } from '../../../layouts/BranchContext'; // fix path
import {
  Button,
  Container,
  FormControl,
  InputLabel,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Select,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Modal,
  Tooltip,
  useTheme,
  CircularProgress,
  Chip,
  InputAdornment,
  ListSubheader,
  Badge,
} from '@mui/material';

import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import CloseIcon from '@mui/icons-material/Close';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Alert = React.forwardRef(function Alert(props, ref) {
  return (
    <MuiAlert
      elevation={6}
      ref={ref}
      variant="filled"
      {...props}
    />
  );
});

// ─────────────────────────────────────────────────────────────
// Country Codes
// ─────────────────────────────────────────────────────────────

const COUNTRY_CODES = [
  { code: '+91', country: 'IN', flag: '🇮🇳' },
  { code: '+1', country: 'US', flag: '🇺🇸' },
  { code: '+44', country: 'GB', flag: '🇬🇧' },
  { code: '+61', country: 'AU', flag: '🇦🇺' },
  { code: '+971', country: 'AE', flag: '🇦🇪' },
  { code: '+65', country: 'SG', flag: '🇸🇬' },
  { code: '+60', country: 'MY', flag: '🇲🇾' },
  { code: '+966', country: 'SA', flag: '🇸🇦' },
  { code: '+974', country: 'QA', flag: '🇶🇦' },
  { code: '+968', country: 'OM', flag: '🇴🇲' },
  { code: '+973', country: 'BH', flag: '🇧🇭' },
  { code: '+965', country: 'KW', flag: '🇰🇼' },
  { code: '+64', country: 'NZ', flag: '🇳🇿' },
  { code: '+49', country: 'DE', flag: '🇩🇪' },
  { code: '+33', country: 'FR', flag: '🇫🇷' },
  { code: '+81', country: 'JP', flag: '🇯🇵' },
  { code: '+86', country: 'CN', flag: '🇨🇳' },
  { code: '+92', country: 'PK', flag: '🇵🇰' },
  { code: '+880', country: 'BD', flag: '🇧🇩' },
  { code: '+94', country: 'LK', flag: '🇱🇰' },
  { code: '+977', country: 'NP', flag: '🇳🇵' },
  { code: '+27', country: 'ZA', flag: '🇿🇦' },
  { code: '+234', country: 'NG', flag: '🇳🇬' },
  { code: '+254', country: 'KE', flag: '🇰🇪' },
];

// ─────────────────────────────────────────────────────────────
// Split stored mobile number
// Example: +919876543210
// => { code: '+91', number: '9876543210' }
// ─────────────────────────────────────────────────────────────

const splitMobileNumber = (raw = '') => {
  if (!raw) {
    return {
      code: '+91',
      number: '',
    };
  }

  const cleaned = String(raw).trim();

  const match = COUNTRY_CODES
    .slice()
    .sort((a, b) => b.code.length - a.code.length)
    .find((country) => cleaned.startsWith(country.code));

  if (match) {
    return {
      code: match.code,
      number: cleaned.slice(match.code.length).replace(/\D/g, ''),
    };
  }

  return {
    code: '+91',
    number: cleaned.replace(/\D/g, ''),
  };
};

// ─────────────────────────────────────────────────────────────
// Course Name
// ─────────────────────────────────────────────────────────────

const getCourseName = (courseId, courseData) => {
  const course = courseData.find(
    (courseItem) => courseItem.id === courseId
  );

  return (
    course?.name ||
    course?.course_name ||
    `Course ${courseId}`
  );
};

// ─────────────────────────────────────────────────────────────
// Country Code Select
// ─────────────────────────────────────────────────────────────

const CountryCodeSelect = ({
  value,
  onChange,
  theme,
}) => (
  <InputAdornment position="start" sx={{ mr: 0 }}>
    <Select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      variant="standard"
      disableUnderline
      sx={{
        minWidth: 80,
        fontSize: '0.875rem',

        '& .MuiSelect-select': {
          py: 0,
          pr: '24px !important',
          pl: 0.5,
          display: 'flex',
          alignItems: 'center',
        },

        '& .MuiSelect-icon': {
          right: 0,
        },
      }}
      MenuProps={{
        PaperProps: {
          sx: {
            maxHeight: 300,
          },
        },
      }}
    >
      {COUNTRY_CODES.map((country, index) => (
        <MenuItem
          key={`${country.code}-${country.country}-${index}`}
          value={country.code}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <span>{country.flag}</span>

            <Typography variant="body2">
              {country.code}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ ml: 0.25 }}
            >
              {country.country}
            </Typography>
          </Box>
        </MenuItem>
      ))}
    </Select>

    <Box
      sx={{
        width: '1px',
        height: '24px',
        backgroundColor: theme.palette.divider,
        mx: 1,
      }}
    />
  </InputAdornment>
);

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────

const EditEnquiry = () => {
  const theme = useTheme();

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [enquiryId, setEnquiryId] = useState(null);

  const [branches, setBranches] = useState([]);

    const { selectedBranchId } = useBranch();

  const [organizationId] = useState(() => {
    try {
      const sessionUser = JSON.parse(
        sessionStorage.getItem('user') || '{}'
      );

      return sessionUser.organization_id || '';
    } catch (error) {
      return '';
    }
  });

  // ─────────────────────────────────────────────────────────────
  // Country Codes
  // ─────────────────────────────────────────────────────────────

  const [mobileCountryCode, setMobileCountryCode] =
    useState('+91');

  const [altMobileCountryCode, setAltMobileCountryCode] =
    useState('+91');

  // FIX: Referral phone country code
  const [referPhoneCountryCode, setReferPhoneCountryCode] =
    useState('+91');

  // ─────────────────────────────────────────────────────────────
  // Course / Batch
  // ─────────────────────────────────────────────────────────────

  const [courseOpen, setCourseOpen] = useState(false);
  const [batchOpen, setBatchOpen] = useState(false);

  const [tempCourse, setTempCourse] = useState([]);
  const [tempBatch, setTempBatch] = useState([]);

  const [filteredBatches, setFilteredBatches] = useState([]);

  const [statusData, setStatusData] = useState([]);

  const [showDemoModal, setShowDemoModal] = useState(false);

  const [tempDemoCourse, setTempDemoCourse] = useState([]);
  const [tempDemoBatch, setTempDemoBatch] = useState([]);

  const [filteredDemoBatches, setFilteredDemoBatches] =
    useState([]);

  const [demoCourseOpen, setDemoCourseOpen] = useState(false);
  const [demoBatchOpen, setDemoBatchOpen] = useState(false);

  const [facultyData, setFacultyData] = useState([]);
  const [batchData, setBatchData] = useState([]);

  const [filteredDemoFaculty, setFilteredDemoFaculty] =
    useState([]);

  // ─────────────────────────────────────────────────────────────
  // Form Data
  // ─────────────────────────────────────────────────────────────

  const [formData, setFormData] = useState({
    sendMail: false,
    sendTextSMS: false,
    corporate: false,

    enquiryCode: '',
    enquiryDate: new Date()
      .toISOString()
      .split('T')[0],

    clientName: '',
    aadharCardNo: '',

    mobileNo: '',
    alternateMobileNo: '',

    email: '',
    address: '',

    nextFollowUp: new Date(
      Date.now() + 7 * 24 * 60 * 60 * 1000
    )
      .toISOString()
      .split('T')[0],

    preferredTime: '',

    followUpMedium: '',
    enquirySource: '',
    assignedTo: '',
    status: '',
    nationality: '',

    remark: '',

    course: [],

    referName: '',
    referEmail: '',
    referPhone: '',

    anyPersonToEnroll: false,

    scheduleDemoFlag: false,

    demoDate: '',
    demoTime: '',

    demoCourse: [],
    demoBatch: [],
    demoFaculty: [],
  });

  // ─────────────────────────────────────────────────────────────
  // Dropdown Data
  // ─────────────────────────────────────────────────────────────

  const [followUpMediumData, setFollowUpMediumData] =
    useState([]);

  const [enquirySourceData, setEnquirySourceData] =
    useState([]);

  const [assignedToData, setAssignedToData] =
    useState([]);

  const [nationalityData, setNationalityData] =
    useState([]);

  const [courseData, setCourseData] =
    useState([]);

  // ─────────────────────────────────────────────────────────────
  // Load Initial Data
  // ─────────────────────────────────────────────────────────────

  useEffect(() => {
    console.log('🔄 EditEnquiry mounted');
    console.log('📍 location.state:', location.state);
    console.log('📍 URL pathname:', location.pathname);
    console.log('📍 URL params:', params);

    const enquiryData =
      location.state?.enquiryData;

    const idFromState =
      location.state?.enquiryId ||
      location.state?.id ||
      location.state?.enquiry?.id;

    const idFromParams = params.id;

    const pathSegments =
      location.pathname.split('/');

    const lastSegment =
      pathSegments[pathSegments.length - 1];

    const idFromUrl =
      !isNaN(lastSegment) && lastSegment !== ''
        ? parseInt(lastSegment)
        : null;

    const finalId =
      idFromState ||
      idFromParams ||
      idFromUrl;

    console.log(
      '🆔 Final ID to use:',
      finalId
    );

    if (finalId) {
      setEnquiryId(finalId);
    }

       const loadData = async () => {
      const sessionUser = JSON.parse(
        sessionStorage.getItem('user') || '{}'
      );

      const orgId = String(
        sessionUser.organization_id || ''
      );

      if (!selectedBranchId || !orgId) {
        console.warn(
          'loadData: missing branchId or orgId'
        );
      }

      await fetchAllDropdownData(
        selectedBranchId,
        orgId
      );

      if (enquiryData) {
        console.log(
          '✅ Prefilling form with enquiry data from state'
        );

        if (
          enquiryData.id &&
          !finalId
        ) {
          setEnquiryId(enquiryData.id);
        }

        prefillFormData(enquiryData);
      } else if (finalId) {
        console.log(
          '🔍 Fetching enquiry:',
          finalId
        );

        const fetchedData =
          await fetchEnquiryData(finalId);

        if (fetchedData) {
          prefillFormData(fetchedData);
        }
      } else {
        setSnackbarMessage(
          'No enquiry ID provided. Please select an enquiry to edit.'
        );

        setSnackbarSeverity('error');
        setOpenSnackbar(true);

        setTimeout(() => {
          navigate(-1);
        }, 2000);
      }

      setIsLoading(false);
    };

    loadData();
  }, [
    location.state,
    location.pathname,
    params,
  ]);

  // ─────────────────────────────────────────────────────────────
  // Fetch Dropdown Data
  // ─────────────────────────────────────────────────────────────

  const fetchAllDropdownData = async (
  branchId = selectedBranchId,
  orgId = organizationId
) => {
  if (!branchId || !orgId) {
    console.warn(
      'fetchAllDropdownData skipped:',
      { branchId, orgId }
    );
    return;
  }

  const accessToken = localStorage.getItem('accessToken');

  try {
    // ======================================================
    // 1. FETCH DESIGNATIONS FIRST (to find real Counselor ID)
    // ======================================================

    const designationsRes = await fetch(
      `${API_BASE_URL}/api/master/designations/`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    let counselorIds = [];

    if (designationsRes.ok) {
      const designationsData = await designationsRes.json();

      let designationsList = [];

      if (designationsData && Array.isArray(designationsData.data)) {
        designationsList = designationsData.data;
      } else if (Array.isArray(designationsData)) {
        designationsList = designationsData;
      } else if (Array.isArray(designationsData.results)) {
        designationsList = designationsData.results;
      }

      const counselorDesignations = designationsList.filter(
        (designation) => {
          const name = (
            designation.name ||
            designation.designation_name ||
            ''
          ).toLowerCase().trim();

          return (
            name === 'counselor' ||
            name === 'counsellor' ||
            name === 'counselors' ||
            name === 'counsellors'
          );
        }
      );

      counselorIds = counselorDesignations
        .map((designation) => designation.id || designation.designationId)
        .filter((id) => id !== null && id !== undefined);

      console.log('✅ Counselor designation IDs:', counselorIds);
    }

    // ======================================================
    // 2. FETCH THE REST OF THE DROPDOWN DATA
    //    (removed the hardcoded designation_id=1 fetch)
    // ======================================================

    const [
      followUpMediumRes,
      enquirySourceRes,
      nationalityRes,
      courseRes,
      batchRes,
      facultyRes,
      statusRes,
    ] = await Promise.all([
      fetch(`${API_BASE_URL}/api/master/followup-mediums/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      }),
      fetch(`${API_BASE_URL}/api/master/source/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      }),
      fetch(`${API_BASE_URL}/api/master/nationalities/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      }),
      fetch(`${API_BASE_URL}/api/course/courses/?organization=${orgId}&branch=${branchId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      }),
      fetch(`${API_BASE_URL}/api/course/batches/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      }),
      // All employees (for trainers / faculty)
      fetch(`${API_BASE_URL}/api/staff/employees/?organization_id=${orgId}&branch_id=${branchId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      }),
      fetch(`${API_BASE_URL}/api/master/status/`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      }),
    ]);

    const followUpMediumDataJson = await followUpMediumRes.json();
    const enquirySourceDataJson = await enquirySourceRes.json();
    const nationalityDataJson = await nationalityRes.json();
    const courseDataJson = await courseRes.json();
    const batchDataJson = await batchRes.json();
    const facultyDataJson = await facultyRes.json();
    const statusDataJson = await statusRes.json();

    setFollowUpMediumData(
      followUpMediumDataJson.data || followUpMediumDataJson || []
    );

    setEnquirySourceData(
      enquirySourceDataJson.data || enquirySourceDataJson || []
    );

    setNationalityData(
      nationalityDataJson.data || nationalityDataJson || []
    );

    setCourseData(
      courseDataJson.results ||
      courseDataJson.data ||
      (Array.isArray(courseDataJson) ? courseDataJson : [])
    );

    setBatchData(batchDataJson.data || batchDataJson || []);
    setStatusData(statusDataJson.data || statusDataJson || []);

    // ======================================================
    // 3. FACULTY / TRAINERS (from the full employee list)
    // ======================================================

    let employeesList = [];

    if (facultyDataJson && Array.isArray(facultyDataJson.results)) {
      employeesList = facultyDataJson.results;
    } else if (facultyDataJson && Array.isArray(facultyDataJson.data)) {
      employeesList = facultyDataJson.data;
    } else if (Array.isArray(facultyDataJson)) {
      employeesList = facultyDataJson;
    }

    const trainers = employeesList.filter((employee) => {
      const designation = (
        employee.designation_name ||
        employee.designation ||
        ''
      ).toLowerCase();

      return designation.includes('trainer');
    });

    setFacultyData(trainers);

    // ======================================================
    // 4. ASSIGNED TO = COUNSELORS (using the real designation IDs)
    // ======================================================

    let counselors = [];

    if (counselorIds.length > 0) {
      const counselorResponses = await Promise.all(
        counselorIds.map(async (designationId) => {
          try {
            const response = await fetch(
              `${API_BASE_URL}/api/staff/employees/filter/?organization_id=${orgId}&branch_id=${branchId}&designation_id=${designationId}`,
              { headers: { Authorization: `Bearer ${accessToken}` } }
            );

            if (!response.ok) return [];

            const data = await response.json();

            if (Array.isArray(data)) return data;
            if (Array.isArray(data?.data)) return data.data;
            if (Array.isArray(data?.results)) return data.results;
            return [];
          } catch (error) {
            console.error(
              `Error fetching counselor designation ${designationId}:`,
              error
            );
            return [];
          }
        })
      );

      counselors = counselorResponses.flat();
    }

    const uniqueCounselors = counselors.filter(
      (counselor, index, self) =>
        index === self.findIndex(
          (item) =>
            String(item.id || item.employeeId) ===
            String(counselor.id || counselor.employeeId)
        )
    );

    setAssignedToData(uniqueCounselors);

    console.log('✅ Dropdown data loaded');
  } catch (error) {
    console.error('❌ Error fetching dropdown data:', error);

    setSnackbarMessage(
      'Failed to load dropdown data. Please refresh the page.'
    );
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
  }
};

  // ─────────────────────────────────────────────────────────────
  // Fetch Enquiry
  // ─────────────────────────────────────────────────────────────

  const fetchEnquiryData = async (id) => {
    const accessToken =
      localStorage.getItem('accessToken');

    try {
          const response = await fetch(
        `${API_BASE_URL}/api/student_details/enquiries/${id}/?organization=${organizationId}&branch=${selectedBranchId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        const errorText =
          await response.text();

        console.error(
          '❌ API Error:',
          errorText
        );

        throw new Error(
          `Failed to fetch enquiry data (Status: ${response.status})`
        );
      }

      const data =
        await response.json();

      console.log(
        '✅ Enquiry data:',
        data
      );

      return data;
    } catch (error) {
      console.error(
        '❌ Error fetching enquiry:',
        error
      );

      setSnackbarMessage(
        `Failed to load enquiry data: ${error.message}`
      );

      setSnackbarSeverity('error');
      setOpenSnackbar(true);

      return null;
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Prefill Form
  // ─────────────────────────────────────────────────────────────

  const prefillFormData = (
    enquiryData
  ) => {
    console.log(
      '📋 Prefilling form:',
      enquiryData
    );

    const mobile =
      splitMobileNumber(
        enquiryData.mobile_no || ''
      );

    const altMobile =
      splitMobileNumber(
        enquiryData.alternate_mobile_no || ''
      );

    // FIX:
    // Referral phone is now handled exactly like mobile number.
    const referPhone =
      splitMobileNumber(
        enquiryData.refer_phone || ''
      );

    setMobileCountryCode(
      mobile.code
    );

    setAltMobileCountryCode(
      altMobile.code
    );

    // FIX:
    // Restore referral country code from API.
    setReferPhoneCountryCode(
      referPhone.code
    );

    const coursesArray =
      Array.isArray(enquiryData.courses)
        ? enquiryData.courses
        : [];

    const demoCoursesArray =
      enquiryData.demo_course
        ? [enquiryData.demo_course]
        : [];

    const demoBatchesArray =
      enquiryData.demo_batch
        ? [enquiryData.demo_batch]
        : [];

    let demoFacultyArray = [];

    if (
      enquiryData.demo_faculty !== null &&
      enquiryData.demo_faculty !== undefined
    ) {
      if (
        Array.isArray(
          enquiryData.demo_faculty
        )
      ) {
        demoFacultyArray =
          enquiryData.demo_faculty
            .map((id) => parseInt(id))
            .filter(Boolean);
      } else {
        const parsed =
          parseInt(
            enquiryData.demo_faculty
          );

        if (!isNaN(parsed)) {
          demoFacultyArray = [parsed];
        }
      }
    }

    setFormData({
      sendMail:
        enquiryData.send_mail ||
        false,

      sendTextSMS:
        enquiryData.send_sms ||
        false,

      corporate:
        enquiryData.corporate ||
        false,

      enquiryCode:
        enquiryData.enquiry_code ||
        '',

      enquiryDate:
        enquiryData.enquiry_date ||
        new Date()
          .toISOString()
          .split('T')[0],

      clientName:
        enquiryData.candidate_name ||
        '',

      aadharCardNo:
        enquiryData.aadhar_card_no ||
        '',

      mobileNo:
        mobile.number,

      alternateMobileNo:
        altMobile.number,

      email:
        enquiryData.email ||
        '',

      address:
        enquiryData.address ||
        '',

      nextFollowUp:
        enquiryData.next_followup_date ||
        new Date(
          Date.now() +
          7 * 24 * 60 * 60 * 1000
        )
          .toISOString()
          .split('T')[0],

      preferredTime:
        enquiryData.preferred_time ||
        '',

      followUpMedium:
        enquiryData.followup_medium ||
        '',

      enquirySource:
        enquiryData.enquiry_source ||
        '',

      assignedTo:
        enquiryData.assigned_to ||
        '',

      status:
        enquiryData.status ||
        '',

      nationality:
        enquiryData.nationality ||
        '',

      remark:
        enquiryData.remark ||
        '',

      course:
        coursesArray,

      referName:
        enquiryData.refer_name ||
        '',

      referEmail:
        enquiryData.refer_email ||
        '',

      // FIX:
      // Only number part stored in input.
      referPhone:
        referPhone.number,

      anyPersonToEnroll:
        !!(
          enquiryData.refer_name ||
          enquiryData.refer_email ||
          enquiryData.refer_phone
        ),

      scheduleDemoFlag:
        enquiryData.schedule_demo ||
        false,

      demoDate:
        enquiryData.demo_date ||
        '',

      demoTime:
        enquiryData.demo_time ||
        '',

      demoCourse:
        demoCoursesArray,

      demoBatch:
        demoBatchesArray,

      demoFaculty:
        demoFacultyArray,
    });
  };

  // ─────────────────────────────────────────────────────────────
  // Referral Person
  // ─────────────────────────────────────────────────────────────

  const handleAnyPersonChange = (e) => {
    const value =
      e.target.value === 'yes';

    setFormData((prev) => ({
      ...prev,

      anyPersonToEnroll:
        value,

      referName:
        value
          ? prev.referName
          : '',

      referEmail:
        value
          ? prev.referEmail
          : '',

      // FIX:
      // Clear referral phone when No selected.
      referPhone:
        value
          ? prev.referPhone
          : '',
    }));

    // FIX:
    // Reset referral country code when referral is disabled.
    if (!value) {
      setReferPhoneCountryCode('+91');
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Demo Checkbox
  // ─────────────────────────────────────────────────────────────

  const handleDemoCheckboxChange = (e) => {
    const checked =
      e.target.checked;

    setFormData((prev) => ({
      ...prev,
      scheduleDemoFlag:
        checked,
    }));

    if (checked) {
      setTempDemoCourse([
        ...formData.demoCourse,
      ]);

      setTempDemoBatch([
        ...formData.demoBatch,
      ]);

      setShowDemoModal(true);
    } else {
      setFormData((prev) => ({
        ...prev,

        scheduleDemoFlag:
          false,

        demoCourse: [],
        demoBatch: [],
        demoFaculty: [],
        demoDate: '',
        demoTime: '',
      }));

      setTempDemoCourse([]);
      setTempDemoBatch([]);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Demo Batch Filtering
  // ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (
      tempDemoCourse.length === 0
    ) {
      setFilteredDemoBatches([]);
      setTempDemoBatch([]);
    } else {
      const filtered =
        batchData.filter(
          (batch) =>
            tempDemoCourse.includes(
              batch.course
            )
        );

      setFilteredDemoBatches(
        filtered
      );

      const validSelectedBatches =
        tempDemoBatch.filter(
          (batchId) =>
            filtered.some(
              (batch) =>
                batch.id ===
                parseInt(batchId)
            )
        );

      if (
        validSelectedBatches.length !==
        tempDemoBatch.length
      ) {
        setTempDemoBatch(
          validSelectedBatches
        );
      }
    }
  }, [
    tempDemoCourse,
    batchData,
  ]);

  // ─────────────────────────────────────────────────────────────
  // Demo Faculty Filtering
  // ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (
      tempDemoCourse.length === 0 ||
      tempDemoBatch.length === 0
    ) {
      setFilteredDemoFaculty([]);
      return;
    }

    const facultyIds =
      new Set();

    // Batch fields
    tempDemoBatch.forEach(
      (batchId) => {
        const batch =
          batchData.find(
            (item) =>
              item.id ===
              parseInt(batchId)
          );

        if (!batch) return;

        [
          batch.trainer,
          batch.faculty,
          batch.trainer_id,
          batch.faculty_id,
          batch.trainer_details?.id,
          batch.faculty_details?.id,
        ]
          .filter(
            (id) =>
              id !== undefined &&
              id !== null
          )
          .forEach((id) =>
            facultyIds.add(
              parseInt(id)
            )
          );

        const arrFields = [
          batch.trainers,
          batch.faculties,
          batch.assigned_faculty,
        ];

        arrFields.forEach(
          (arr) => {
            if (!Array.isArray(arr)) {
              return;
            }

            arr.forEach(
              (item) => {
                const id =
                  typeof item ===
                  'object'
                    ? item?.id
                    : item;

                if (
                  id !== undefined &&
                  id !== null
                ) {
                  facultyIds.add(
                    parseInt(id)
                  );
                }
              }
            );
          }
        );
      }
    );

    // Course fields
    if (
      facultyIds.size === 0
    ) {
      tempDemoCourse.forEach(
        (courseId) => {
          const course =
            courseData.find(
              (item) =>
                item.id ===
                parseInt(courseId)
            );

          if (!course) return;

          [
            course.trainer,
            course.faculty,
            course.trainer_id,
            course.faculty_id,
            course.trainer_details?.id,
            course.faculty_details?.id,
          ]
            .filter(
              (id) =>
                id !== undefined &&
                id !== null
            )
            .forEach((id) =>
              facultyIds.add(
                parseInt(id)
              )
            );

          const arrFields = [
            course.trainers,
            course.faculties,
            course.assigned_faculty,
          ];

          arrFields.forEach(
            (arr) => {
              if (
                !Array.isArray(arr)
              ) {
                return;
              }

              arr.forEach(
                (item) => {
                  const id =
                    typeof item ===
                    'object'
                      ? item?.id
                      : item;

                  if (
                    id !== undefined &&
                    id !== null
                  ) {
                    facultyIds.add(
                      parseInt(id)
                    );
                  }
                }
              );
            }
          );
        }
      );
    }

    console.log(
      '🎓 Faculty IDs:',
      [...facultyIds]
    );

    if (
      facultyIds.size === 0
    ) {
      setFilteredDemoFaculty(
        facultyData
      );
    } else {
      const matched =
        facultyData.filter(
          (faculty) =>
            facultyIds.has(
              faculty.id
            )
        );

      setFilteredDemoFaculty(
        matched.length > 0
          ? matched
          : facultyData
      );

      setFormData((prev) => {
        if (
          !prev.demoFaculty ||
          prev.demoFaculty.length === 0
        ) {
          return prev;
        }

        const validFaculty =
          prev.demoFaculty.filter(
            (facultyId) =>
              matched.some(
                (faculty) =>
                  faculty.id ===
                  parseInt(facultyId)
              )
          );

        if (
          validFaculty.length !==
          prev.demoFaculty.length
        ) {
          return {
            ...prev,
            demoFaculty:
              validFaculty,
          };
        }

        return prev;
      });
    }
  }, [
    tempDemoCourse,
    tempDemoBatch,
    batchData,
    courseData,
    facultyData,
  ]);

  // ─────────────────────────────────────────────────────────────
  // Demo Batch Grouping
  // ─────────────────────────────────────────────────────────────

  const getDemoBatchesGroupedByCourse =
    () => {
      if (
        tempDemoCourse.length === 0
      ) {
        return {};
      }

      const grouped = {};

      const filteredBatches =
        batchData.filter(
          (batch) =>
            tempDemoCourse.includes(
              batch.course
            )
        );

      filteredBatches.forEach(
        (batch) => {
          const courseName =
            getCourseName(
              batch.course,
              courseData
            );

          if (!grouped[courseName]) {
            grouped[courseName] = [];
          }

          grouped[courseName].push(
            batch
          );
        }
      );

      return grouped;
    };

  // ─────────────────────────────────────────────────────────────
  // Save Demo
  // ─────────────────────────────────────────────────────────────

  const handleSaveDemoDetails =
    () => {
      setFormData((prev) => ({
        ...prev,

        demoCourse:
          tempDemoCourse,

        demoBatch:
          tempDemoBatch,
      }));

      setShowDemoModal(false);
    };

  // ─────────────────────────────────────────────────────────────
  // General Handle Change
  // ─────────────────────────────────────────────────────────────

  const handleChange = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    // Mobile + Alternate + Referral
    if (
      name === 'mobileNo' ||
      name === 'alternateMobileNo' ||
      name === 'referPhone'
    ) {
      const digitsOnly =
        value
          .replace(/\D/g, '')
          .slice(0, 10);

      setFormData((prev) => ({
        ...prev,
        [name]: digitsOnly,
      }));

      return;
    }

    // Aadhaar
    if (
      name === 'aadharCardNo'
    ) {
      const digitsOnly =
        value
          .replace(/\D/g, '')
          .slice(0, 12);

      setFormData((prev) => ({
        ...prev,
        [name]: digitsOnly,
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,

      [name]:
        type === 'checkbox'
          ? checked
          : value,
    }));
  };

  // ─────────────────────────────────────────────────────────────
  // Course Change
  // ─────────────────────────────────────────────────────────────

  const handleCourseChange = (
    event
  ) => {
    const { value } =
      event.target;

    setTempCourse(
      typeof value === 'string'
        ? value.split(',')
        : value
    );
  };

  // ─────────────────────────────────────────────────────────────
  // Update Enquiry
  // ─────────────────────────────────────────────────────────────

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!enquiryId) {
      setSnackbarMessage(
        'Enquiry ID is missing. Cannot update.'
      );

      setSnackbarSeverity('error');
      setOpenSnackbar(true);

      return;
    }

    // Aadhaar validation
    if (
      formData.aadharCardNo &&
      formData.aadharCardNo.length !== 12
    ) {
      setSnackbarMessage(
        'Aadhaar number must be exactly 12 digits!'
      );

      setSnackbarSeverity('error');
      setOpenSnackbar(true);

      return;
    }

    // Mobile validation
    if (
      formData.mobileNo.length !== 10
    ) {
      setSnackbarMessage(
        'Mobile number must be exactly 10 digits!'
      );

      setSnackbarSeverity('error');
      setOpenSnackbar(true);

      return;
    }

    // FIX:
    // Referral phone validation
    if (
      formData.anyPersonToEnroll &&
      formData.referPhone &&
      formData.referPhone.length !== 10
    ) {
      setSnackbarMessage(
        'Referral phone number must be exactly 10 digits!'
      );

      setSnackbarSeverity('error');
      setOpenSnackbar(true);

      return;
    }

    // Required fields
    if (
      !formData.clientName ||
      !formData.mobileNo ||
      !formData.email ||
      !formData.address ||
      !formData.followUpMedium ||
      !formData.enquirySource ||
      !formData.assignedTo ||
      !formData.status ||
      !formData.nationality ||
      !formData.course.length
    ) {
      setSnackbarMessage(
        'Please fill all required fields!'
      );

      setSnackbarSeverity('error');
      setOpenSnackbar(true);

      return;
    }

    // Referral required fields
    if (
      formData.anyPersonToEnroll
    ) {
      if (
        !formData.referName ||
        !formData.referEmail ||
        !formData.referPhone
      ) {
        setSnackbarMessage(
          'Please fill all referral details!'
        );

        setSnackbarSeverity('error');
        setOpenSnackbar(true);

        return;
      }

      if (
        formData.referPhone.length !==
        10
      ) {
        setSnackbarMessage(
          'Referral phone number must be exactly 10 digits!'
        );

        setSnackbarSeverity('error');
        setOpenSnackbar(true);

        return;
      }
    }

    setIsSubmitting(true);

    try {
      const payload = {
        enquiry_date:
          formData.enquiryDate,

        candidate_name:
          formData.clientName,

        aadhar_card_no:
          formData.aadharCardNo ||
          null,

        mobile_no:
          `${mobileCountryCode}${formData.mobileNo}`,

        alternate_mobile_no:
          formData.alternateMobileNo
            ? `${altMobileCountryCode}${formData.alternateMobileNo}`
            : null,

        email:
          formData.email,

        address:
          formData.address,

        next_followup_date:
          formData.nextFollowUp,

        preferred_time:
          formData.preferredTime ||
          null,

        remark:
          formData.remark,

        refer_name:
          formData.anyPersonToEnroll
            ? (
                formData.referName ||
                null
              )
            : null,

        refer_email:
          formData.anyPersonToEnroll
            ? (
                formData.referEmail ||
                null
              )
            : null,

        // ─────────────────────────────────────
        // FIX:
        // Referral phone now includes country code
        // ─────────────────────────────────────

        refer_phone:
          formData.anyPersonToEnroll &&
          formData.referPhone
            ? `${referPhoneCountryCode}${formData.referPhone}`
            : null,

        send_mail:
          formData.sendMail,

        send_sms:
          formData.sendTextSMS,

        schedule_demo:
          formData.scheduleDemoFlag,

        demo_date:
          formData.scheduleDemoFlag
            ? (
                formData.demoDate ||
                null
              )
            : null,

        demo_time:
          formData.scheduleDemoFlag
            ? (
                formData.demoTime ||
                null
              )
            : null,

        followup_medium:
          parseInt(
            formData.followUpMedium
          ),

        enquiry_source:
          parseInt(
            formData.enquirySource
          ),

        assigned_to:
          parseInt(
            formData.assignedTo
          ),

        status:
          parseInt(
            formData.status
          ),

        nationality:
          parseInt(
            formData.nationality
          ),

        demo_course:
          formData.scheduleDemoFlag &&
          formData.demoCourse.length > 0
            ? parseInt(
                formData.demoCourse[0]
              )
            : null,

        demo_batch:
          formData.scheduleDemoFlag &&
          formData.demoBatch.length > 0
            ? parseInt(
                formData.demoBatch[0]
              )
            : null,

        demo_faculty:
          formData.scheduleDemoFlag &&
          formData.demoFaculty.length > 0
            ? parseInt(
                formData.demoFaculty[0]
              )
            : null,

        courses:
          formData.course.map(
            (id) => parseInt(id)
          ),
      };

      // Remove null values
      Object.keys(payload).forEach(
        (key) => {
          if (
            payload[key] === null ||
            (
              Array.isArray(
                payload[key]
              ) &&
              payload[key].length === 0
            )
          ) {
            delete payload[key];
          }
        }
      );

      console.log(
        '📤 Sending UPDATE payload:',
        payload
      );

      const accessToken =
        localStorage.getItem(
          'accessToken'
        );

           const response = await fetch(
        `${API_BASE_URL}/api/student_details/enquiries/${enquiryId}/?organization=${organizationId}&branch=${selectedBranchId}`,
        {
          method: 'PUT',

          headers: {
            'Content-Type':
              'application/json',

            Authorization:
              `Bearer ${accessToken}`,
          },

          body:
            JSON.stringify(payload),
        }
      );

      console.log(
        '📊 Response status:',
        response.status
      );

      if (!response.ok) {
        let errorMessage =
          'Failed to update enquiry';

        try {
          const responseText =
            await response.text();

          console.error(
            '❌ API Error:',
            responseText
          );

          try {
            const errorData =
              JSON.parse(
                responseText
              );

            if (
              errorData.error
            ) {
              errorMessage =
                errorData.error;
            } else if (
              errorData.message
            ) {
              errorMessage =
                errorData.message;
            } else if (
              errorData.detail
            ) {
              errorMessage =
                errorData.detail;
            } else if (
              typeof errorData ===
              'object'
            ) {
              errorMessage =
                Object.entries(
                  errorData
                )
                  .map(
                    ([field, errors]) =>
                      `${field}: ${
                        Array.isArray(errors)
                          ? errors.join(', ')
                          : errors
                      }`
                  )
                  .join('; ') ||
                JSON.stringify(
                  errorData
                );
            }
          } catch {
            errorMessage =
              responseText ||
              `Server error: ${response.status}`;
          }
        } catch {
          errorMessage =
            `Server error: ${response.status} - Unable to read error response`;
        }

        throw new Error(
          errorMessage
        );
      }

      const result =
        await response.json();

      console.log(
        '✅ Success response:',
        result
      );

      setShowSuccessModal(true);

      setSnackbarMessage(
        'Enquiry updated successfully!'
      );

      setSnackbarSeverity(
        'success'
      );

      setOpenSnackbar(true);
    } catch (error) {
      console.error(
        '💥 Error updating enquiry:',
        error
      );

      setSnackbarMessage(
        error.message ||
        'Failed to update enquiry. Please try again.'
      );

      setSnackbarSeverity(
        'error'
      );

      setOpenSnackbar(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Cancel
  // ─────────────────────────────────────────────────────────────

  const handleCancel = () => {
    navigate(-1);
  };

  // ─────────────────────────────────────────────────────────────
  // Loading
  // ─────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          my: 4,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={60} />
      </Container>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // Styles
  // ─────────────────────────────────────────────────────────────

  const fieldStyle = {
    mb: 3,

    '& .MuiInputLabel-root': {
      color:
        theme.palette.text.primary,
    },

    '& .MuiInputLabel-root.Mui-focused':
      {
        color:
          theme.palette.primary.main,
      },

    '& .MuiOutlinedInput-notchedOutline':
      {
        borderColor:
          theme.palette.text.primary,
      },

    '&:hover .MuiOutlinedInput-notchedOutline':
      {
        borderColor:
          theme.palette.primary.main,
      },
  };

  const dateFieldStyle = {
    ...fieldStyle,

    '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator':
      {
        filter:
          theme.palette.mode ===
          'dark'
            ? 'invert(1)'
            : 'none',
      },
  };

  const timeFieldStyle = {
    ...fieldStyle,

    '& input::-webkit-calendar-picker-indicator':
      {
        filter:
          theme.palette.mode ===
          'dark'
            ? 'invert(1)'
            : 'none',
      },
  };

  // ─────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────

  return (
    <Container
      maxWidth="lg"
      sx={{ my: 0 }}
    >
      <Box
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 2,
          backgroundColor:
            theme.palette.background.paper,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent:
              'space-between',
            alignItems: 'center',
            mb: 3,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 'bold',
              color:
                theme.palette.primary.main,
            }}
          >
            Edit Enquiry{' '}
            {enquiryId &&
              `(ID: ${enquiryId})`}
          </Typography>
        </Box>

        <form
          onSubmit={handleUpdate}
        >
          <Grid
            container
            spacing={2}
          >
            {/* ═══════════════════════════════════════
                LEFT COLUMN
            ═══════════════════════════════════════ */}

            <Grid
              item
              xs={12}
              md={6}
            >
              {/* Checkboxes */}
              <FormGroup
                row
                sx={{ mb: 3 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      name="sendMail"
                      checked={
                        formData.sendMail
                      }
                      onChange={
                        handleChange
                      }
                      sx={{
                        color:
                          theme.palette
                            .mode ===
                          'dark'
                            ? '#fff'
                            : theme.palette
                                .text
                                .primary,

                        '&.Mui-checked':
                          {
                            color:
                              theme.palette
                                .primary
                                .main,
                          },
                      }}
                    />
                  }
                  label="Send Mail"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      name="sendTextSMS"
                      checked={
                        formData.sendTextSMS
                      }
                      onChange={
                        handleChange
                      }
                      sx={{
                        color:
                          theme.palette
                            .mode ===
                          'dark'
                            ? '#fff'
                            : theme.palette
                                .text
                                .primary,

                        '&.Mui-checked':
                          {
                            color:
                              theme.palette
                                .primary
                                .main,
                          },
                      }}
                    />
                  }
                  label="Send Text SMS"
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      name="corporate"
                      checked={
                        formData.corporate
                      }
                      onChange={
                        handleChange
                      }
                      sx={{
                        color:
                          theme.palette
                            .mode ===
                          'dark'
                            ? '#fff'
                            : theme.palette
                                .text
                                .primary,

                        '&.Mui-checked':
                          {
                            color:
                              theme.palette
                                .primary
                                .main,
                          },
                      }}
                    />
                  }
                  label="Corporate"
                />
              </FormGroup>

              {/* Enquiry Code */}
              <TextField
                label="Enquiry Code"
                variant="outlined"
                fullWidth
                disabled
                name="enquiryCode"
                value={
                  formData.enquiryCode
                }
                onChange={
                  handleChange
                }
                sx={{
                  ...fieldStyle,

                  '& .Mui-disabled':
                    {
                      backgroundColor:
                        theme.palette
                          .mode ===
                        'dark'
                          ? 'rgba(255,255,255,0.05)'
                          : 'rgba(0,0,0,0.05)',
                    },
                }}
              />

              {/* Enquiry Date */}
              <TextField
                label="Enquiry Date"
                type="date"
                variant="outlined"
                fullWidth
                name="enquiryDate"
                value={
                  formData.enquiryDate
                }
                onChange={
                  handleChange
                }
                InputLabelProps={{
                  shrink: true,
                }}
                sx={
                  dateFieldStyle
                }
              />

              {/* Candidate Name */}
              <TextField
                label="Candidate Name"
                variant="outlined"
                fullWidth
                required
                name="clientName"
                value={
                  formData.clientName
                }
                onChange={
                  handleChange
                }
                sx={fieldStyle}
              />

              {/* Aadhaar */}
              <TextField
                label="Aadhar Card No."
                variant="outlined"
                fullWidth
                name="aadharCardNo"
                value={
                  formData.aadharCardNo
                }
                onChange={
                  handleChange
                }
                inputProps={{
                  maxLength: 12,
                  inputMode:
                    'numeric',
                }}
                helperText={
                  formData.aadharCardNo
                    .length > 0 &&
                  formData.aadharCardNo
                    .length !== 12
                    ? `Aadhaar number must be exactly 12 digits (currently ${formData.aadharCardNo.length}/12)`
                    : ''
                }
                error={
                  formData.aadharCardNo
                    .length > 0 &&
                  formData.aadharCardNo
                    .length !== 12
                }
                sx={fieldStyle}
              />

              {/* Mobile */}
              <TextField
                label="Mobile No."
                variant="outlined"
                fullWidth
                required
                name="mobileNo"
                value={
                  formData.mobileNo
                }
                onChange={
                  handleChange
                }
                inputProps={{
                  maxLength: 10,
                  inputMode:
                    'numeric',
                }}
                helperText={
                  formData.mobileNo
                    .length > 0 &&
                  formData.mobileNo
                    .length < 10
                    ? `${
                        10 -
                        formData.mobileNo
                          .length
                      } more digit(s) required`
                    : ''
                }
                InputProps={{
                  startAdornment: (
                    <CountryCodeSelect
                      value={
                        mobileCountryCode
                      }
                      onChange={
                        setMobileCountryCode
                      }
                      theme={
                        theme
                      }
                    />
                  ),
                }}
                sx={fieldStyle}
              />

              {/* Alternate Mobile */}
              <TextField
                label="Alternate Mobile No."
                variant="outlined"
                fullWidth
                name="alternateMobileNo"
                value={
                  formData.alternateMobileNo
                }
                onChange={
                  handleChange
                }
                inputProps={{
                  maxLength: 10,
                  inputMode:
                    'numeric',
                }}
                helperText={
                  formData
                    .alternateMobileNo
                    .length > 0 &&
                  formData
                    .alternateMobileNo
                    .length < 10
                    ? `${
                        10 -
                        formData
                          .alternateMobileNo
                          .length
                      } more digit(s) required`
                    : ''
                }
                InputProps={{
                  startAdornment: (
                    <CountryCodeSelect
                      value={
                        altMobileCountryCode
                      }
                      onChange={
                        setAltMobileCountryCode
                      }
                      theme={
                        theme
                      }
                    />
                  ),
                }}
                sx={fieldStyle}
              />

              {/* Email */}
              <TextField
                label="Email"
                type="email"
                variant="outlined"
                fullWidth
                required
                name="email"
                value={
                  formData.email
                }
                onChange={
                  handleChange
                }
                sx={fieldStyle}
              />

              {/* Address */}
              <TextField
                label="Address"
                variant="outlined"
                fullWidth
                required
                name="address"
                value={
                  formData.address
                }
                onChange={
                  handleChange
                }
                sx={fieldStyle}
              />

              {/* Referral Question */}
              <FormControl
                component="fieldset"
                sx={{ mb: 2 }}
              >
                <Typography
                  component="legend"
                  sx={{
                    color:
                      theme.palette
                        .text
                        .primary,
                    mb: 1,
                  }}
                >
                  Any Person You Would Like
                  To Enroll? *
                </Typography>

                <RadioGroup
                  row
                  name="anyPersonToEnroll"
                  value={
                    formData.anyPersonToEnroll
                      ? 'yes'
                      : 'no'
                  }
                  onChange={
                    handleAnyPersonChange
                  }
                >
                  <FormControlLabel
                    value="yes"
                    control={
                      <Radio
                        sx={{
                          color:
                            theme.palette
                              .text
                              .primary,
                        }}
                      />
                    }
                    label="Yes"
                  />

                  <FormControlLabel
                    value="no"
                    control={
                      <Radio
                        sx={{
                          color:
                            theme.palette
                              .text
                              .primary,
                        }}
                      />
                    }
                    label="No"
                  />
                </RadioGroup>
              </FormControl>

              {/* ═══════════════════════════════════════
                  REFERRAL DETAILS
              ═══════════════════════════════════════ */}

              {formData.anyPersonToEnroll && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection:
                      'column',
                    gap: 2,
                    mb: 2,
                    pl: 2,
                    borderLeft:
                      `2px solid ${theme.palette.primary.main}`,
                  }}
                >
                  {/* Referral Name */}
                  <TextField
                    label="Refer Name *"
                    variant="outlined"
                    fullWidth
                    name="referName"
                    value={
                      formData.referName
                    }
                    onChange={
                      handleChange
                    }
                    required
                    sx={fieldStyle}
                  />

                  {/* Referral Email */}
                  <TextField
                    label="Refer Email *"
                    type="email"
                    variant="outlined"
                    fullWidth
                    name="referEmail"
                    value={
                      formData.referEmail
                    }
                    onChange={
                      handleChange
                    }
                    required
                    sx={fieldStyle}
                  />

                  {/* ═══════════════════════════════════
                      FIXED REFERRAL PHONE
                  ═══════════════════════════════════ */}

                  <TextField
                    label="Refer Phone *"
                    variant="outlined"
                    fullWidth
                    required
                    name="referPhone"
                    value={
                      formData.referPhone
                    }
                    onChange={
                      handleChange
                    }
                    inputProps={{
                      maxLength: 10,
                      inputMode:
                        'numeric',
                    }}
                    error={
                      formData.referPhone
                        .length > 0 &&
                      formData.referPhone
                        .length !== 10
                    }
                    helperText={
                      formData.referPhone
                        .length > 0 &&
                      formData.referPhone
                        .length < 10
                        ? `${
                            10 -
                            formData
                              .referPhone
                              .length
                          } more digit(s) required`
                        : ''
                    }
                    InputProps={{
                      startAdornment: (
                        <CountryCodeSelect
                          value={
                            referPhoneCountryCode
                          }
                          onChange={
                            setReferPhoneCountryCode
                          }
                          theme={
                            theme
                          }
                        />
                      ),
                    }}
                    sx={fieldStyle}
                  />
                </Box>
              )}
            </Grid>

            {/* ═══════════════════════════════════════
                RIGHT COLUMN
            ═══════════════════════════════════════ */}

            <Grid
              item
              xs={12}
              md={6}
            >
              {/* Next Follow Up */}
              <TextField
                label="Next Follow Up"
                type="date"
                variant="outlined"
                fullWidth
                name="nextFollowUp"
                value={
                  formData.nextFollowUp
                }
                onChange={
                  handleChange
                }
                InputLabelProps={{
                  shrink: true,
                }}
                sx={
                  dateFieldStyle
                }
              />

              {/* Preferred Time */}
              <TextField
                label="Preferred Time"
                type="time"
                variant="outlined"
                fullWidth
                name="preferredTime"
                value={
                  formData.preferredTime
                }
                onChange={
                  handleChange
                }
                InputLabelProps={{
                  shrink: true,
                }}
                sx={
                  timeFieldStyle
                }
              />

              {/* Follow Up Medium */}
              <FormControl
                fullWidth
                required
                sx={fieldStyle}
              >
                <InputLabel>
                  Follow Up Medium
                </InputLabel>

                <Select
                  name="followUpMedium"
                  value={
                    formData.followUpMedium
                  }
                  label="Follow Up Medium"
                  onChange={
                    handleChange
                  }
                >
                  <MenuItem value="">
                    <em>
                      Select a medium
                    </em>
                  </MenuItem>

                  {followUpMediumData.map(
                    (medium) => (
                      <MenuItem
                        key={medium.id}
                        value={medium.id}
                      >
                        {medium.name}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>

              {/* Enquiry Source */}
              <FormControl
                fullWidth
                required
                sx={fieldStyle}
              >
                <InputLabel>
                  Enquiry Source
                </InputLabel>

                <Select
                  name="enquirySource"
                  value={
                    formData.enquirySource
                  }
                  label="Enquiry Source"
                  onChange={
                    handleChange
                  }
                >
                  <MenuItem value="">
                    <em>
                      Select a source
                    </em>
                  </MenuItem>

                  {enquirySourceData.map(
                    (source) => (
                      <MenuItem
                        key={source.id}
                        value={source.id}
                      >
                        {source.name}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>

              {/* Assigned To */}
              <FormControl
                fullWidth
                required
                sx={fieldStyle}
              >
                <InputLabel>
                  Assigned To
                </InputLabel>

                <Select
                  name="assignedTo"
                  value={
                    formData.assignedTo
                  }
                  label="Assigned To"
                  onChange={
                    handleChange
                  }
                >
                  <MenuItem value="">
                    <em>
                      Select an assignee
                    </em>
                  </MenuItem>

                  {assignedToData.map(
                    (assignee) => (
                      <MenuItem
                        key={
                          assignee.id
                        }
                        value={
                          assignee.id
                        }
                      >
                        {assignee.name ||
                          `${
                            assignee.first_name ||
                            ''
                          } ${
                            assignee.last_name ||
                            ''
                          }`.trim() ||
                          assignee.username ||
                          `Employee ${assignee.id}`}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>

              {/* Status */}
              <FormControl
                fullWidth
                required
                sx={fieldStyle}
              >
                <InputLabel>
                  Status
                </InputLabel>

                <Select
                  name="status"
                  value={
                    formData.status
                  }
                  label="Status"
                  onChange={
                    handleChange
                  }
                >
                  <MenuItem value="">
                    <em>
                      Select a status
                    </em>
                  </MenuItem>

                  {statusData.map(
                    (status) => (
                      <MenuItem
                        key={status.id}
                        value={status.id}
                      >
                        {status.name}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>

              {/* Nationality */}
              <FormControl
                fullWidth
                required
                sx={fieldStyle}
              >
                <InputLabel>
                  Nationality
                </InputLabel>

                <Select
                  name="nationality"
                  value={
                    formData.nationality
                  }
                  label="Nationality"
                  onChange={
                    handleChange
                  }
                >
                  <MenuItem value="">
                    <em>
                      Select a nationality
                    </em>
                  </MenuItem>

                  {nationalityData.map(
                    (nationality) => (
                      <MenuItem
                        key={
                          nationality.id
                        }
                        value={
                          nationality.id
                        }
                      >
                        {
                          nationality.name
                        }
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>

              {/* Courses */}
              <FormControl
                fullWidth
                required
                sx={fieldStyle}
              >
                <InputLabel
                  shrink={
                    formData.course
                      .length > 0
                  }
                >
                  Courses
                </InputLabel>

                <Select
                  multiple
                  value={
                    tempCourse.length >
                    0
                      ? tempCourse
                      : formData.course
                  }
                  open={
                    courseOpen
                  }
                  onOpen={() => {
                    setTempCourse([
                      ...formData.course,
                    ]);

                    setCourseOpen(
                      true
                    );
                  }}
                  label="Courses"
                  renderValue={(
                    selected
                  ) => (
                    <Box
                      sx={{
                        display:
                          'flex',
                        flexWrap:
                          'wrap',
                        gap: 0.5,
                      }}
                    >
                      {selected.map(
                        (value) => {
                          const course =
                            courseData.find(
                              (item) =>
                                item.id ===
                                value
                            );

                          return (
                            <Chip
                              key={
                                value
                              }
                              label={
                                course?.name ||
                                course?.course_name ||
                                `Course ${value}`
                              }
                              size="small"
                              onDelete={() => {
                                setTempCourse(
                                  tempCourse.filter(
                                    (
                                      id
                                    ) =>
                                      id !==
                                      value
                                  )
                                );
                              }}
                              deleteIcon={
                                <CloseIcon />
                              }
                              sx={{
                                backgroundColor:
                                  theme
                                    .palette
                                    .primary
                                    .main,
                                color:
                                  theme
                                    .palette
                                    .primary
                                    .contrastText,
                              }}
                            />
                          );
                        }
                      )}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 400,
                      },
                    },
                  }}
                >
                  {courseData.map(
                    (course) => (
                      <MenuItem
                        key={
                          course.id
                        }
                        value={
                          course.id
                        }
                      >
                        <Checkbox
                          checked={
                            tempCourse.indexOf(
                              course.id
                            ) > -1
                          }
                          onChange={(
                            e
                          ) => {
                            e.stopPropagation();

                            const currentIndex =
                              tempCourse.indexOf(
                                course.id
                              );

                            const newSelected =
                              [
                                ...tempCourse,
                              ];

                            if (
                              currentIndex ===
                              -1
                            ) {
                              newSelected.push(
                                course.id
                              );
                            } else {
                              newSelected.splice(
                                currentIndex,
                                1
                              );
                            }

                            setTempCourse(
                              newSelected
                            );
                          }}
                        />

                        {course.name ||
                          course.course_name ||
                          `Course ${course.id}`}
                      </MenuItem>
                    )
                  )}

                  <ListSubheader
                    sx={{
                      position:
                        'sticky',
                      bottom: 0,
                      bgcolor:
                        theme
                          .palette
                          .background
                          .paper,
                      borderTop:
                        `1px solid ${theme.palette.divider}`,
                      p: 1,
                      display:
                        'flex',
                      justifyContent:
                        'flex-end',
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        setFormData(
                          (prev) => ({
                            ...prev,

                            course:
                              tempCourse,

                            demoCourse:
                              tempCourse.length >
                              0
                                ? [
                                    tempCourse[0],
                                  ]
                                : [],
                          })
                        );

                        setCourseOpen(
                          false
                        );
                      }}
                    >
                      OK
                    </Button>
                  </ListSubheader>
                </Select>
              </FormControl>

              {/* Schedule Demo */}
              <FormControlLabel
                control={
                  <Checkbox
                    name="scheduleDemoFlag"
                    checked={
                      formData.scheduleDemoFlag
                    }
                    onChange={
                      handleDemoCheckboxChange
                    }
                  />
                }
                label="Schedule a Demo Class"
                sx={{
                  mt: 2,
                  mb: 2,
                }}
              />

              {/* Demo Summary */}
              {formData.scheduleDemoFlag && (
                <Box
                  sx={{
                    mb: 3,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor:
                      theme.palette
                        .mode ===
                      'dark'
                        ? 'rgba(25, 118, 210, 0.1)'
                        : 'rgba(25, 118, 210, 0.05)',
                    border:
                      `1px solid ${theme.palette.primary.main}`,
                  }}
                >
                  <Box
                    sx={{
                      display:
                        'flex',
                      justifyContent:
                        'space-between',
                      alignItems:
                        'center',
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight:
                          'bold',
                        color:
                          theme.palette
                            .primary
                            .main,
                      }}
                    >
                      Demo Class Details
                    </Typography>

                    <Button
                      size="small"
                      onClick={() => {
                        setTempDemoCourse([
                          ...formData.demoCourse,
                        ]);

                        setTempDemoBatch([
                          ...formData.demoBatch,
                        ]);

                        setShowDemoModal(
                          true
                        );
                      }}
                    >
                      Edit Demo
                    </Button>
                  </Box>

                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid
                      item
                      xs={12}
                      sm={6}
                    >
                      <Typography
                        variant="caption"
                      >
                        Course(s)
                      </Typography>

                      <Typography
                        variant="body2"
                      >
                        {formData.demoCourse
                          .length > 0
                          ? formData.demoCourse
                              .map(
                                (
                                  courseId
                                ) => {
                                  const course =
                                    courseData.find(
                                      (
                                        item
                                      ) =>
                                        item.id ===
                                        courseId
                                    );

                                  return (
                                    course?.course_name ||
                                    course?.name ||
                                    `Course ${courseId}`
                                  );
                                }
                              )
                              .join(', ')
                          : 'Not selected'}
                      </Typography>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={6}
                    >
                      <Typography
                        variant="caption"
                      >
                        Batch(es)
                      </Typography>

                      <Typography
                        variant="body2"
                      >
                        {formData.demoBatch
                          .length > 0
                          ? formData.demoBatch
                              .map(
                                (
                                  batchId
                                ) => {
                                  const batch =
                                    batchData.find(
                                      (
                                        item
                                      ) =>
                                        item.id ===
                                        batchId
                                    );

                                  return (
                                    batch?.batch_name ||
                                    batch?.name ||
                                    `Batch ${batchId}`
                                  );
                                }
                              )
                              .join(', ')
                          : 'Not selected'}
                      </Typography>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={4}
                    >
                      <Typography
                        variant="caption"
                      >
                        Faculty
                      </Typography>

                      <Typography
                        variant="body2"
                      >
                        {formData.demoFaculty
                          .length > 0
                          ? formData.demoFaculty
                              .map(
                                (
                                  facultyId
                                ) => {
                                  const faculty =
                                    facultyData.find(
                                      (
                                        item
                                      ) =>
                                        item.id ===
                                        parseInt(
                                          facultyId
                                        )
                                    );

                                  return (
                                    faculty?.name ||
                                    `${faculty?.first_name || ''} ${faculty?.last_name || ''}`.trim() ||
                                    `Faculty ${facultyId}`
                                  );
                                }
                              )
                              .join(', ')
                          : 'Not selected'}
                      </Typography>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={4}
                    >
                      <Typography
                        variant="caption"
                      >
                        Demo Date
                      </Typography>

                      <Typography
                        variant="body2"
                      >
                        {formData.demoDate
                          ? new Date(
                              formData.demoDate
                            ).toLocaleDateString()
                          : 'Not selected'}
                      </Typography>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      sm={4}
                    >
                      <Typography
                        variant="caption"
                      >
                        Demo Time
                      </Typography>

                      <Typography
                        variant="body2"
                      >
                        {formData.demoTime ||
                          'Not selected'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Remark */}
              <TextField
                label="Remark"
                variant="outlined"
                fullWidth
                name="remark"
                value={
                  formData.remark
                }
                onChange={
                  handleChange
                }
                sx={fieldStyle}
              />
            </Grid>
          </Grid>

          {/* ═══════════════════════════════════════
              ACTION BUTTONS
          ═══════════════════════════════════════ */}

          <Box
            sx={{
              display: 'flex',
              justifyContent:
                'flex-end',
              gap: 2,
              mt: 3,
            }}
          >
            <Tooltip
              title={
                !formData.clientName
                  ? 'Enter Candidate Name'
                  : !formData.mobileNo
                  ? 'Enter Mobile No.'
                  : formData.mobileNo.length !==
                    10
                  ? 'Mobile number must be 10 digits'
                  : !formData.email
                  ? 'Enter Email'
                  : !formData.address
                  ? 'Enter Address'
                  : !formData.followUpMedium
                  ? 'Select Follow Up Medium'
                  : !formData.enquirySource
                  ? 'Select Enquiry Source'
                  : !formData.assignedTo
                  ? 'Select Assigned To'
                  : !formData.status
                  ? 'Select Status'
                  : !formData.nationality
                  ? 'Select Nationality'
                  : !formData.course.length
                  ? 'Select at least one Course'
                  : formData.anyPersonToEnroll &&
                    !formData.referName
                  ? 'Enter Refer Name'
                  : formData.anyPersonToEnroll &&
                    !formData.referEmail
                  ? 'Enter Refer Email'
                  : formData.anyPersonToEnroll &&
                    !formData.referPhone
                  ? 'Enter Refer Phone'
                  : formData.anyPersonToEnroll &&
                    formData.referPhone.length !==
                      10
                  ? 'Referral phone number must be 10 digits'
                  : formData.aadharCardNo &&
                    formData.aadharCardNo.length !==
                      12
                  ? 'Aadhaar number must be 12 digits'
                  : ''
              }
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  type="submit"
                  disabled={
                    isSubmitting ||
                    !formData.clientName ||
                    !formData.mobileNo ||
                    formData.mobileNo.length !==
                      10 ||
                    !formData.email ||
                    !formData.address ||
                    !formData.followUpMedium ||
                    !formData.enquirySource ||
                    !formData.assignedTo ||
                    !formData.status ||
                    !formData.nationality ||
                    !formData.course.length ||
                    (
                      formData.anyPersonToEnroll &&
                      (
                        !formData.referName ||
                        !formData.referEmail ||
                        !formData.referPhone ||
                        formData.referPhone.length !==
                          10
                      )
                    ) ||
                    (
                      formData.aadharCardNo &&
                      formData.aadharCardNo.length !==
                        12
                    )
                  }
                  sx={{
                    textTransform:
                      'none',
                    fontWeight:
                      'bold',
                    padding:
                      '8px 16px',
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress
                      size={24}
                      color="inherit"
                    />
                  ) : (
                    'UPDATE'
                  )}
                </Button>
              </span>
            </Tooltip>

            <Button
              variant="outlined"
              color="error"
              onClick={
                handleCancel
              }
              disabled={
                isSubmitting
              }
              sx={{
                textTransform:
                  'none',
                fontWeight:
                  'bold',
                padding:
                  '8px 16px',
              }}
            >
              CANCEL
            </Button>
          </Box>
        </form>
      </Box>

      {/* ═══════════════════════════════════════════════════════
          DEMO MODAL
      ═══════════════════════════════════════════════════════ */}

      <Modal
        open={
          showDemoModal
        }
        onClose={() =>
          setShowDemoModal(false)
        }
        BackdropProps={{
          style: {
            backgroundColor:
              'rgba(0, 0, 0, 0.8)',
          },
        }}
      >
        <Box
          sx={{
            position:
              'absolute',
            top: '50%',
            left: '50%',
            transform:
              'translate(-50%, -50%)',
            width: {
              xs: '95%',
              sm: 700,
              md: 800,
            },
            maxHeight:
              '90vh',
            bgcolor:
              theme.palette
                .background
                .paper,
            borderRadius: 2,
            boxShadow: 24,
            overflow:
              'hidden',
            display:
              'flex',
            flexDirection:
              'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              backgroundColor:
                theme.palette
                  .primary
                  .main,
              padding:
                '12px 20px',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight:
                  'bold',
              }}
            >
              Schedule Demo Class
            </Typography>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              overflowY:
                'auto',
              padding: 3,
            }}
          >
            <Box
              sx={{
                display:
                  'flex',
                flexDirection:
                  'column',
                gap: 3,
              }}
            >
              {/* Demo Course */}
              <FormControl
                fullWidth
                required
              >
                <InputLabel>
                  Course *
                </InputLabel>

                <Select
                  multiple
                  value={
                    tempDemoCourse
                  }
                  label="Course *"
                  onChange={(e) => {
                    const {
                      value,
                    } = e.target;

                    setTempDemoCourse(
                      typeof value ===
                        'string'
                        ? value.split(
                            ','
                          )
                        : value
                    );
                  }}
                  renderValue={(
                    selected
                  ) => (
                    <Box
                      sx={{
                        display:
                          'flex',
                        flexWrap:
                          'wrap',
                        gap: 0.5,
                      }}
                    >
                      {selected.map(
                        (value) => {
                          const course =
                            courseData.find(
                              (
                                item
                              ) =>
                                item.id ===
                                value
                            );

                          return (
                            <Chip
                              key={
                                value
                              }
                              label={
                                course?.course_name ||
                                course?.name ||
                                `Course ${value}`
                              }
                              size="small"
                              onDelete={() =>
                                setTempDemoCourse(
                                  tempDemoCourse.filter(
                                    (
                                      id
                                    ) =>
                                      id !==
                                      value
                                  )
                                )
                              }
                              deleteIcon={
                                <CloseIcon />
                              }
                            />
                          );
                        }
                      )}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  {courseData.map(
                    (course) => (
                      <MenuItem
                        key={
                          course.id
                        }
                        value={
                          course.id
                        }
                      >
                        <Checkbox
                          checked={
                            tempDemoCourse.indexOf(
                              course.id
                            ) > -1
                          }
                        />

                        <Typography>
                          {course.course_name ||
                            course.name ||
                            `Course ${course.id}`}
                        </Typography>
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>

              {/* Demo Batch */}
              <FormControl
                fullWidth
                required
                disabled={
                  tempDemoCourse.length ===
                  0
                }
              >
                <InputLabel>
                  Batch *
                </InputLabel>

                <Select
                  multiple
                  value={
                    tempDemoBatch
                  }
                  label="Batch *"
                  disabled={
                    tempDemoCourse.length ===
                    0
                  }
                  onChange={(e) => {
                    const {
                      value,
                    } = e.target;

                    setTempDemoBatch(
                      typeof value ===
                        'string'
                        ? value.split(
                            ','
                          )
                        : value
                    );
                  }}
                  renderValue={(
                    selected
                  ) => (
                    <Box
                      sx={{
                        display:
                          'flex',
                        flexWrap:
                          'wrap',
                        gap: 0.5,
                      }}
                    >
                      {selected.map(
                        (value) => {
                          const batch =
                            batchData.find(
                              (
                                item
                              ) =>
                                item.id ===
                                value
                            );

                          return (
                            <Chip
                              key={
                                value
                              }
                              label={
                                batch?.batch_name ||
                                batch?.name ||
                                `Batch ${value}`
                              }
                              size="small"
                              icon={
                                <span>
                                  📚
                                </span>
                              }
                              onDelete={() =>
                                setTempDemoBatch(
                                  tempDemoBatch.filter(
                                    (
                                      id
                                    ) =>
                                      id !==
                                      value
                                  )
                                )
                              }
                              deleteIcon={
                                <CloseIcon />
                              }
                            />
                          );
                        }
                      )}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 400,
                      },
                    },
                  }}
                >
                  {tempDemoCourse.length ===
                  0 ? (
                    <MenuItem disabled>
                      Select course first
                    </MenuItem>
                  ) : (
                    Object.entries(
                      getDemoBatchesGroupedByCourse()
                    ).map(
                      ([
                        courseName,
                        courseBatches,
                      ]) => (
                        <Fragment
                          key={
                            courseName
                          }
                        >
                          <ListSubheader>
                            <Box
                              sx={{
                                display:
                                  'flex',
                                alignItems:
                                  'center',
                                gap: 1,
                              }}
                            >
                              <span>
                                🎓
                              </span>

                              <span>
                                {
                                  courseName
                                }
                              </span>

                              <Badge
                                badgeContent={
                                  courseBatches.length
                                }
                                color="primary"
                              />
                            </Box>
                          </ListSubheader>

                          {courseBatches.map(
                            (
                              batch
                            ) => (
                              <MenuItem
                                key={
                                  batch.id
                                }
                                value={
                                  batch.id
                                }
                              >
                                <Checkbox
                                  checked={
                                    tempDemoBatch.indexOf(
                                      batch.id
                                    ) >
                                    -1
                                  }
                                />

                                <Typography>
                                  {batch.batch_name ||
                                    batch.name ||
                                    `Batch ${batch.id}`}
                                </Typography>
                              </MenuItem>
                            )
                          )}
                        </Fragment>
                      )
                    )
                  )}
                </Select>
              </FormControl>

              {/* Demo Faculty */}
              <FormControl
                fullWidth
                required
                disabled={
                  tempDemoCourse.length ===
                    0 ||
                  tempDemoBatch.length ===
                    0
                }
              >
                <InputLabel>
                  Faculty *
                </InputLabel>

                <Select
                  multiple
                  value={
                    formData.demoFaculty
                  }
                  label="Faculty *"
                  onChange={(e) => {
                    const {
                      value,
                    } = e.target;

                    setFormData(
                      (prev) => ({
                        ...prev,

                        demoFaculty:
                          typeof value ===
                          'string'
                            ? value.split(
                                ','
                              )
                            : value,
                      })
                    );
                  }}
                  renderValue={(
                    selected
                  ) => (
                    <Box
                      sx={{
                        display:
                          'flex',
                        flexWrap:
                          'wrap',
                        gap: 0.5,
                      }}
                    >
                      {selected.map(
                        (facultyId) => {
                          const faculty =
                            facultyData.find(
                              (
                                item
                              ) =>
                                item.id ===
                                parseInt(
                                  facultyId
                                )
                            );

                          return (
                            <Chip
                              key={
                                facultyId
                              }
                              label={
                                faculty?.name ||
                                `${faculty?.first_name || ''} ${faculty?.last_name || ''}`.trim() ||
                                `Faculty ${facultyId}`
                              }
                              size="small"
                              onDelete={() => {
                                setFormData(
                                  (
                                    prev
                                  ) => ({
                                    ...prev,

                                    demoFaculty:
                                      prev.demoFaculty.filter(
                                        (
                                          id
                                        ) =>
                                          id !==
                                          facultyId
                                      ),
                                  })
                                );
                              }}
                              deleteIcon={
                                <CloseIcon />
                              }
                            />
                          );
                        }
                      )}
                    </Box>
                  )}
                >
                  {filteredDemoFaculty.length ===
                  0 ? (
                    <MenuItem disabled>
                      No faculty available
                    </MenuItem>
                  ) : (
                    filteredDemoFaculty.map(
                      (
                        faculty
                      ) => (
                        <MenuItem
                          key={
                            faculty.id
                          }
                          value={
                            faculty.id
                          }
                        >
                          <Checkbox
                            checked={
                              formData.demoFaculty.indexOf(
                                faculty.id
                              ) >
                              -1
                            }
                          />

                          <Typography>
                            {faculty.name ||
                              `${faculty.first_name || ''} ${faculty.last_name || ''}`.trim() ||
                              `Faculty ${faculty.id}`}
                          </Typography>
                        </MenuItem>
                      )
                    )
                  )}
                </Select>
              </FormControl>

              {/* Demo Date */}
              <TextField
                label="Demo Date"
                type="date"
                variant="outlined"
                fullWidth
                name="demoDate"
                value={
                  formData.demoDate
                }
                onChange={
                  handleChange
                }
                InputLabelProps={{
                  shrink: true,
                }}
                required
                sx={
                  dateFieldStyle
                }
              />

              {/* Demo Time */}
              <TextField
                label="Demo Time"
                type="time"
                variant="outlined"
                fullWidth
                name="demoTime"
                value={
                  formData.demoTime
                }
                onChange={
                  handleChange
                }
                InputLabelProps={{
                  shrink: true,
                }}
                required
                sx={
                  timeFieldStyle
                }
              />
            </Box>
          </Box>

          {/* Footer */}
          <Box
            sx={{
              display:
                'flex',
              justifyContent:
                'flex-end',
              gap: 2,
              p: 2,
              borderTop:
                `1px solid ${theme.palette.divider}`,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setShowDemoModal(
                  false
                );

                setTempDemoCourse(
                  []
                );

                setTempDemoBatch(
                  []
                );
              }}
             data-app-cancel="true">
              Cancel
            </Button>

            <Button
              variant="contained"
              onClick={
                handleSaveDemoDetails
              }
              disabled={
                tempDemoCourse.length ===
                  0 ||
                tempDemoBatch.length ===
                  0 ||
                !formData.demoFaculty
                  .length ||
                !formData.demoDate ||
                !formData.demoTime
              }
            >
              Apply Demo Details
            </Button>
          </Box>
        </Box>
      </Modal>

      {/* ═══════════════════════════════════════════════════════
          SUCCESS MODAL
      ═══════════════════════════════════════════════════════ */}

      <Modal
        open={
          showSuccessModal
        }
        onClose={() =>
          setShowSuccessModal(false)
        }
        BackdropProps={{
          style: {
            backgroundColor:
              'rgba(0, 0, 0, 0.8)',
          },
        }}
      >
        <Box
          sx={{
            position:
              'absolute',
            top: '50%',
            left: '50%',
            transform:
              'translate(-50%, -50%)',
            width: 400,
            bgcolor:
              theme.palette
                .background
                .paper,
            borderRadius: 2,
            boxShadow: 24,
            overflow:
              'hidden',
          }}
        >
          <Box
            sx={{
              backgroundColor:
                theme.palette
                  .primary
                  .main,
              padding:
                '12px 16px',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color:
                  theme.palette
                    .primary
                    .contrastText,
                fontWeight:
                  'bold',
              }}
            >
              Enquiry Updated ✅
            </Typography>
          </Box>

          <Box
            sx={{
              padding: '20px',
            }}
          >
            <Typography
              sx={{ mb: 2 }}
            >
              Enquiry Updated
              Successfully
            </Typography>

            <Box
              sx={{
                display:
                  'flex',
                justifyContent:
                  'flex-end',
              }}
            >
              <Button
                onClick={() => {
                  setShowSuccessModal(
                    false
                  );

                  navigate(-1);
                }}
                variant="contained"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* ═══════════════════════════════════════════════════════
          SNACKBAR
      ═══════════════════════════════════════════════════════ */}

      <Snackbar
        open={
          openSnackbar
        }
        autoHideDuration={
          4000
        }
        onClose={() =>
          setOpenSnackbar(false)
        }
        anchorOrigin={{
          vertical: 'top',
          horizontal:
            'center',
        }}
      >
        <Alert
          onClose={() =>
            setOpenSnackbar(false)
          }
          severity={
            snackbarSeverity
          }
          sx={{
            width: '100%',
          }}
        >
          {
            snackbarMessage
          }
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditEnquiry;